import { BenefitType } from "../../../common/interfaces/enums/BenefitType";
import EmploymentType from "../../../common/interfaces/enums/EmploymentType";
import { Gender } from "../../../common/interfaces/enums/Gender";
import { PaymentFrequency } from "../../../common/interfaces/enums/PaymentFrequency";
import PaymentPeriod from "../../../common/interfaces/enums/PaymentPeriod";
import { PremiumStructure } from "../../../common/interfaces/enums/PremiumStructure";
import { ProductType } from "../../../common/interfaces/enums/ProductType";
import QuoteErrorType from "../../enums/QuoteErrorType";
import { SortBy } from "../../enums/SortBy";
import TraumaType from "../../../common/interfaces/enums/TraumaType";

// Payment Frequency
function mapRemotePaymentFrequency(freq: number | string): PaymentFrequency {
    switch (freq?.toString().toLowerCase()) {
        case "1":
        case "yearly":
            return PaymentFrequency.Yearly;
        case "4":
        case "monthly":
            return PaymentFrequency.Monthly;
        case "5":
        case "fortnightly":
            return PaymentFrequency.Fortnightly;
        case "6":
        case "weekly":
            return PaymentFrequency.Weekly;
        default:
            return PaymentFrequency.Unknown;
    }
}

function mapLocalFrequencyToRemote(freq: PaymentFrequency): number {
    switch (freq) {
        case PaymentFrequency.Yearly:
            return 1;
        case PaymentFrequency.Monthly:
            return 4;
        case PaymentFrequency.Weekly:
            return 6;
        case PaymentFrequency.Fortnightly:
        default:
            return 5;
    }
}

function mapRemoteSortBy(sortBy: string | number): SortBy {
    if (typeof sortBy === "string") {
        sortBy = sortBy.toLowerCase();
    }

    switch (sortBy) {
        case 7:
        case "mostpopular":
            return SortBy.MostPopular;
        case 5:
        case "lowestprice":
            return SortBy.LowestPrice;
        case 6:
        case "highestprice":
            return SortBy.HighestPrice;
        case 2:
        case "financialstrength":
            return SortBy.FinancialStrength;
        case 3:
        case "policyfeatures":
            return SortBy.PolicyFeatures;
        case 4:
        case "customerservice":
            return SortBy.CustomerService;
        default:
            return SortBy.LowestPrice;
    }
}

function mapLocalSortBy(sortBy: SortBy): number {
    switch (sortBy) {
        case SortBy.MostPopular:
            return 7;
        case SortBy.HighestPrice:
            return 6;
        case SortBy.LowestPrice:
            return 5;
        case SortBy.FinancialStrength:
            return 2;
        case SortBy.PolicyFeatures:
            return 3;
        case SortBy.CustomerService:
            return 4;
        default:
            return 0;
    }
}

function mapRemoteProduct(product: string | number): ProductType {
    if (typeof product === "string") {
        product = product.toLowerCase();
    }

    switch (product) {
        case 1:
        case "life":
            return ProductType.Life;
        case 3:
        case "health":
            return ProductType.Health;
        case 2:
        case "trauma":
            return ProductType.Trauma;
        case 7:
        case "income":
            return ProductType.Income;
        case 4:
        case 5:
        case "mortgage":
            return ProductType.Mortgage;
        case 8:
        case "wop":
            return ProductType.WaiverOfPremium;
        default:
            return null;
    }
}

const mapRemotePaymentPeriodToLocal = (paymentPeriod: string | number): PaymentPeriod => {
    if (typeof paymentPeriod === "string") {
        paymentPeriod = paymentPeriod.toLowerCase();
    }

    switch (paymentPeriod) {
        case 2:
        case "twoyears":
        case "2 years":
            return PaymentPeriod.TwoYears;
        case 5:
        case "fiveyears":
        case "5 years":
            return PaymentPeriod.FiveYears;
        case 65:
        case "uptoagesixtyfive":
        case "to age 65":
            return PaymentPeriod.UpToAgeSixtyFive;
        case 70:
        case "uptoageseventy":
        case "to age 70":
            return PaymentPeriod.UpToAgeSeventy;

        default:
            return PaymentPeriod.Unknown;
    }
};

const mapRemoteEmploymentTypeToLocal = (
    employmentType: number | string
): EmploymentType => {
    if (typeof employmentType === "string") {
        employmentType = employmentType.toLowerCase();
    }

    switch (employmentType) {
        case 1:
        case "employed":
            return EmploymentType.Employed;
        case 2:
        case "self-employed (over 3 years)":
        case "selfemployedoverthreeyears":
            return EmploymentType.SelfEmployedOverThreeYears;
        case 3:
        case "self-employed (under  3 years)":
        case "selfemployeunderthreeyears":
            return EmploymentType.SelfEmployedUnderThreeYears;
        default:
            return EmploymentType.Unknown;
    }
};

const mapRemotePremiumStructureToLocal = (
    premiumStructure: string | number
): PremiumStructure => {
    if (typeof premiumStructure === "string") {
        premiumStructure = premiumStructure.toLowerCase();
    }

    switch (premiumStructure) {
        case 0:
        case "stepped":
            return PremiumStructure.Stepped;
        case 1:
        case "leveltenyears":
        case "10 years":
            return PremiumStructure.LevelTenYears;
        case 2:
        case "leveltoagesixtyfive":
        case "to age 65":
            return PremiumStructure.LevelToAgeSixtyFive;
        case 3:
        case "leveltoageseventy":
        case "to age 70":
            return PremiumStructure.LevelToAgeSeventy;
        case 4:
        case "leveltoageeighty":
        case "to age 80":
            return PremiumStructure.LevelToAgeEighty;
        default:
            return PremiumStructure.Unknown;
    }
};

const mapRemoteTraumaTypeToLocal = (traumaType: string | number): TraumaType => {
    if (typeof traumaType === "string") {
        traumaType = traumaType.toLowerCase();
    }

    switch (traumaType) {
        case 2:
        case "accelerated":
            return TraumaType.Accelerated;
        case 1:
        case "standalone":
            return TraumaType.Standalone;
        default:
            return TraumaType.Unknown;
    }
};

const mapRemoteGenderToLocal = (gender: number | string): Gender => {
    if (gender === 1 || gender.toString().toLowerCase() === "male") {
        return Gender.Male;
    } else if (gender === 2 || gender.toString().toLowerCase() === "female") {
        return Gender.Female;
    }

    return Gender.Unknown;
};

const mapRemoteBenefitTypeToLocal = (benefit: string | number): BenefitType => {
    if (typeof benefit === "string") {
        benefit = benefit.toLowerCase();
    }

    switch (benefit) {
        case 1:
        case "lifecover":
        case "life cover":
            return BenefitType.LifeCover;
        case 2:
        case "traumacover":
        case "trauma cover":
            return BenefitType.TraumaCover;
        case 3:
        case "healthcover":
        case "health cover":
            return BenefitType.HealthCover;
        case 4:
        case "specialistsandtests":
        case "specialist and test":
            return BenefitType.SpecialistsAndTests;
        case 5:
        case "gpcover":
        case "gp cover":
            return BenefitType.GPCover;
        case 6:
        case "dentalandoptical":
        case "dental and optical":
            return BenefitType.DentalAndOptical;
        case 7:
        case "mortgagerepaymentcover":
        case "mortgage repayment cover":
            return BenefitType.MortgageRepaymentCover;
        case 51:
        case "waiverofpremium":
            return BenefitType.WaiverOfPremium;
        case 10:
        case "incomeprotection":
            return BenefitType.IncomeProtection;
        default:
            return BenefitType.Unknown;
    }
};

const mapRemoteQUoteErrorToLocal = (errorType: number): QuoteErrorType => {
    switch (errorType) {
        case 1:
            return QuoteErrorType.Engine;
        case 2:
            return QuoteErrorType.Validation;
        case 3:
            return QuoteErrorType.MinimumPremium;
        default:
            return QuoteErrorType.Other;
    }
};

export default {
    PaymentFrequency: {
        localToRemote: mapLocalFrequencyToRemote,
        remoteToLocal: mapRemotePaymentFrequency
    },
    SortBy: {
        remoteToLocal: mapRemoteSortBy,
        localToRemote: mapLocalSortBy
    },
    Product: {
        remoteToLocal: mapRemoteProduct
    },
    PaymentPeriod: {
        remoteToLocal: mapRemotePaymentPeriodToLocal
    },
    EmploymentType: {
        remoteToLocal: mapRemoteEmploymentTypeToLocal
    },
    PremiumStructure: {
        remoteToLocal: mapRemotePremiumStructureToLocal
    },
    TraumaType: {
        remoteToLocal: mapRemoteTraumaTypeToLocal
    },
    Gender: {
        remoteToLocal: mapRemoteGenderToLocal
    },
    BenefitType: {
        remoteToLocal: mapRemoteBenefitTypeToLocal
    },
    QuoteErrorType: {
        remoteToLocal: mapRemoteQUoteErrorToLocal
    }
};
